import React, { useMemo, useState } from 'react';
import {
	HealthPlanUser,
	Meta,
	useWorkplaceMetaQuery,
} from '../../../graphql/graphql';
import useNotification from '../../../Hooks/useNotification.hook';
import useTranslation from '../../../Hooks/useTranslation';
import apiClient from '../../../Services/ApiClient';
import NavigationStepper, {
	NavigationStepperComponentParams,
} from '../../Stepper/NavigationStepper';
import HealthPlanCreateConfirmModal from '../../../Modules/HealthPlan/Components/HealthPlanCreateConfirm/HealthPlanCreateConfirmModal';
import { Flyer } from '../../../Modules/EArchive/Enums/Flyer.enum';
import { useMeddiDiabetesCreateSteps } from './Hooks/useMeddiDiabetesCreateSteps';
import { useAppContext } from '../../../Services/AppContext';
import { updateHealthPlanUser } from '../../../Modules/HealthPlan/Services/healthPlan.service';

interface MeddiDiabetesCreateProps {
	patientId: string;
	healthPlanUser: HealthPlanUser;
	onSuccess?: () => void;
}

export default function MeddiDiabetesCreate(props: MeddiDiabetesCreateProps) {
	const [isCreateConfirmModalOpen, setIsCreateConfirmModalOpen] =
		useState(false);
	const { patientId, healthPlanUser, onSuccess } = props;
	const { showNotificationSuccess } = useNotification();
	const { t } = useTranslation();
	const { workplaceId } = useAppContext();
	const workplaceMeta = useWorkplaceMetaQuery({
		id: workplaceId,
	});

	const planIsForFree = useMemo(() => {
		return workplaceMeta?.currentData?.workplace?.meta?.find(
			(item: Meta | null) => item?.key === 'DiabetesForFree',
		);
	}, [workplaceMeta]);

	const handleSetupSuccess = (params: NavigationStepperComponentParams) => {
		params.setActiveStepPath('priceSetup');
	};

	const handleUserInformationSuccess = (
		params: NavigationStepperComponentParams,
	) => {
		params.setActiveStepPath('reviseEntryQuestionnaire');
	};

	const handleInitQuestionnaireSuccess = async (
		data: { questionnaireResultId: string },
		params: NavigationStepperComponentParams,
	) => {
		if (healthPlanUser.healthPlanId) {
			await updateHealthPlanUser({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
				data: {
					questionnaireResultId: data.questionnaireResultId,
				},
			});
			params.setIsLoading(false);
			params.setActiveStepPath('overview');
		}
	};

	const handlePricingSuccess = async (
		params: NavigationStepperComponentParams,
		promoCode?: string,
	) => {
		params.setIsLoading(true);
		if (healthPlanUser.healthPlanId) {
			await updateHealthPlanUser({
				healthPlanId: healthPlanUser.healthPlanId,
				healthPlanUserId: healthPlanUser.id,
				data: {
					ready: true,
					promoCode: promoCode,
				},
			});
		}
		showNotificationSuccess({
			content: t('component.healthPlan.message.created'),
		});
		setIsCreateConfirmModalOpen(true);
		params.setIsLoading(false);
	};

	const afterPlanCreate = () => {
		onSuccess?.();
	};

	const { steps } = useMeddiDiabetesCreateSteps({
		patientId,
		handleInitQuestionnaireSuccess,
		handlePricingSuccess,
		handleSetupSuccess,
		handleUserInformationSuccess,
		planIsForFree,
		healthPlanUser,
	});

	const initialStepPath = useMemo(() => {
		return steps[0]?.key;
	}, [steps]);

	return (
		<>
			<NavigationStepper
				steps={steps}
				initialStepPath={initialStepPath}
				canSkip={false}
			/>
			{isCreateConfirmModalOpen && (
				<HealthPlanCreateConfirmModal
					title='component.healthPlanCreateConfirm.diabetesTitle'
					imageSource='/dist/icons/v2/check-green.svg'
					text={
						t(
							planIsForFree
								? 'component.healthPlanCreateConfirm.diabetesTextForFree'
								: 'component.healthPlanCreateConfirm.diabetesText',
						) ?? ''
					}
					isOpen={isCreateConfirmModalOpen}
					onClose={afterPlanCreate}
					patientId={patientId}
					flyerType={Flyer.MEDDI_DIABETES_LATAM}
				/>
			)}
		</>
	);
}
